import { INSTAGRAM_AUTOMATION_STAGE_STATUSES } from './instagramAutomationStageStatuses'
import {
  InstagramRadioButtonType,
  generateRadioButtonStageDefs,
  waitForSectionElement,
} from './instagramRadioButtonUtils'
import { emailNotificationSettingsUrl } from './instagramUrls'
import { InstagramSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { InstagramRecommendationKey, StageType } from '../../utils/enums'
import { wait } from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  getErrorIsDisabledAndReasonFn,
} from '../recommendationCardDisplayUtils'

const mapSettingToSectionLabel: Record<string, string> = {
  [InstagramSettingKey.EMAIL_NOTIFICATION_ABOUT_SHOPS]: 'Emails about shops',
  [InstagramSettingKey.EMAIL_NOTIFICATION_CART]: 'Cart emails',
  [InstagramSettingKey.EMAIL_NOTIFICATION_FEEDBACK]: 'Feedback emails',
  [InstagramSettingKey.EMAIL_NOTIFICATION_NEWS]: 'News emails',
  [InstagramSettingKey.EMAIL_NOTIFICATION_PRODUCT]: 'Product emails',
  [InstagramSettingKey.EMAIL_NOTIFICATION_PROMOTIONAL]: 'Promotional emails',
  [InstagramSettingKey.EMAIL_NOTIFICATION_REMINDER]: 'Reminder emails',
}

const TARGET_VALUE = 'Off'

function getCurrentValueFn(
  settingsKey: string,
): (initialValues: { [key: string]: any }) => any {
  return (initialValues) => {
    const value = initialValues[settingsKey]

    if (value === undefined || value === null) {
      return null
    }

    return value
  }
}

function getIsFulfilledFn(
  settingsKey: string,
): (initialValues: { [key: string]: any }) => any {
  return (initialValues) => {
    const value = initialValues[settingsKey]

    if (value === undefined || value === null) {
      return false
    }

    return TARGET_VALUE === value
  }
}

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

const getCantUndoFn = () => defaultCantUndoFn

enum StageKey {
  GATHER_EMAIL_NOTIFICATIONS = 'GATHER_EMAIL_NOTIFICATIONS',
  UPDATE_EMAIL_NOTIFICATIONS = 'UPDATE_EMAIL_NOTIFICATIONS',
  UPDATE_EMAIL_NOTIFICATIONS_WAIT = 'UPDATE_EMAIL_NOTIFICATIONS_WAIT',
  UNDO_EMAIL_NOTIFICATIONS = 'UNDO_EMAIL_NOTIFICATIONS',
  UNDO_EMAIL_NOTIFICATIONS_WAIT = 'UNDO_EMAIL_NOTIFICATIONS_WAIT',
}

const { gatherStageDefs, updateStageDefs, undoStageDefs } =
  generateRadioButtonStageDefs(
    InstagramRecommendationKey.INSTAGRAM_EMAIL_NOTIFICATIONS_V2,
    Object.keys(mapSettingToSectionLabel),
    (x) => mapSettingToSectionLabel[x],
    () => TARGET_VALUE,
    () => true,
    StageKey.GATHER_EMAIL_NOTIFICATIONS,
    StageKey.UPDATE_EMAIL_NOTIFICATIONS,
    StageKey.UNDO_EMAIL_NOTIFICATIONS,
    emailNotificationSettingsUrl,
    InstagramRadioButtonType.Input,
  )

/**
 * Currently, email notifications page takes an extra second to load, and if
 * you try to click soon, the page stops working (doesn't register change in
 * setting, clicking other toggles doesn't work as expected).
 *
 * Until we find a more robust solution, we're just introducing a 2 second wait.
 */

updateStageDefs.unshift({
  type: StageType.ACTION,
  key: StageKey.UPDATE_EMAIL_NOTIFICATIONS_WAIT,
  status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
  url: emailNotificationSettingsUrl,
  action: async () => {
    await waitForSectionElement(
      mapSettingToSectionLabel[InstagramSettingKey.EMAIL_NOTIFICATION_FEEDBACK],
      InstagramRadioButtonType.Input,
    )
    await wait(2000)
  },
})

undoStageDefs.unshift({
  type: StageType.ACTION,
  key: StageKey.UNDO_EMAIL_NOTIFICATIONS_WAIT,
  status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
  url: emailNotificationSettingsUrl,
  action: async () => {
    await waitForSectionElement(
      mapSettingToSectionLabel[InstagramSettingKey.EMAIL_NOTIFICATION_FEEDBACK],
      InstagramRadioButtonType.Input,
    )
    await wait(2000)
  },
})

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
